import { useCallback } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import { usePermissions } from './usePermissions';
import type { Permissions } from './usePermissions';
import type { AuthUser, LoginRequest, LoginResponse, LoginResult, Ruolo, UUID } from '@/types';

export interface UseAuthResult extends Permissions {
  user: AuthUser | null;
  /** JWT `ruolo`. Same as `role` from permissions. */
  ruolo: Ruolo | null;
  /** JWT `cliente_id`. Null for internal staff. */
  tenantId: UUID | null;
  isAuthenticated: boolean;
  /** True while bootstrapping or logging in – gate the router on this. */
  isLoading: boolean;
  error: string | null;
  errorCode: string | null;
  login: (credentials: LoginRequest) => Promise<LoginResult>;
  completeLogin: (session: LoginResponse) => Promise<AuthUser>;
  logout: () => Promise<void>;
  clearError: () => void;
}

/**
 * Read/write access to the session plus derived RBAC capabilities.
 *
 * Role and tenant come from the JWT (`/auth/me`); they are never taken from
 * the URL. Components should ask `useAuth().canManageUsers` rather than
 * comparing role strings.
 */
export function useAuth(): UseAuthResult {
  const user = useAuthStore((state) => state.user);
  const status = useAuthStore((state) => state.status);
  const error = useAuthStore((state) => state.error);
  const errorCode = useAuthStore((state) => state.errorCode);
  const storeLogin = useAuthStore((state) => state.login);
  const storeCompleteLogin = useAuthStore((state) => state.completeLogin);
  const storeLogout = useAuthStore((state) => state.logout);
  const clearError = useAuthStore((state) => state.clearError);
  const permissions = usePermissions();

  const login = useCallback((credentials: LoginRequest) => storeLogin(credentials), [storeLogin]);
  const completeLogin = useCallback((session: LoginResponse) => storeCompleteLogin(session), [storeCompleteLogin]);
  const logout = useCallback(() => storeLogout(), [storeLogout]);

  return {
    user,
    ruolo: user?.ruolo ?? null,
    tenantId: user?.cliente_id ?? null,
    isAuthenticated: status === 'authenticated',
    isLoading: status === 'idle' || status === 'loading',
    error,
    errorCode,
    login,
    completeLogin,
    logout,
    clearError,
    ...permissions,
  };
}
